import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import { FFModal } from '~/shared/ui'
import CloseIcon from '@mui/icons-material/Close'
import { useQuery } from '~/shared/hooks/query'
import {
  appointmentCancellationReasons,
  AppointmentCancellationReasonsListResponse,
} from '@fireflyhealth/api'

type CancellationReason = AppointmentCancellationReasonsListResponse[number]

interface CancelData {
  label: string
  children: CancellationReason[]
}

const UidMapping = {
  otherPatient: 'other',
  otherFirefly: 'other_firefly',
}

// Splits the reasons into the two pickers: Firefly-side first, then patient-facing.
const groupCancellationReasons = (reasons: CancellationReason[]): CancelData[] => {
  const fireflyReasons = { label: 'Select Firefly reason', children: [] as CancellationReason[] }
  const patientReasons = { label: 'Select patient reason', children: [] as CancellationReason[] }
  reasons.forEach(reason => {
    const parentValue = reason.is_patient_facing ? patientReasons : fireflyReasons
    parentValue.children.push(reason)
  })
  return [fireflyReasons, patientReasons]
}

const CancelAppointmentModal: FC<{
  otherReasonForPatient: string
  setOtherReasonForPatient: (value: string) => void
  setOtherReasonForFirefly: (value: string) => void
  otherReasonForFirefly: string
  cancellationReasonIds: Array<number>
  setCancellationReasonIds: (val: number[]) => void
  onClose: (success: boolean) => void
}> = props => {
  // controls display for sub options of "Provider not available"
  const [showSubOptions, setShowSubOptions] = useState<boolean>(false)
  // to remove the suboptions when parent is unchecked
  const [selectedSubOptions, setSelectedSubOptions] = useState<Array<number>>([])
  const [showOtherTextField, setShowOtherTextField] = useState<boolean>(false)
  const [showOtherFireflyTextField, setShowOtherFireflyTextField] = useState<boolean>(false)

  /**
   * onReasonCheckboxClick : Function gets triggered when parent set of reason is un/selected
   * @param
   * event: checkbox event
   * reason: selected reason
   */
  const onReasonCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    reason: CancellationReason,
  ) => {
    if (reason.is_patient_facing && reason.uid == UidMapping.otherPatient) {
      setShowOtherTextField(event.target.checked)
      if (!event.target.checked) {
        props.setOtherReasonForPatient('')
      }
    }
    if (reason.children && reason.children.length > 0) {
      setShowSubOptions(event.target.checked)
    }

    addRemoveReasons(reason, event.target.checked, false)
  }

  /**
   * addRemoveReasons : Function to update the state variable cancellationReason
   * @param
   * reasonId: number highlighting the selected reason Id
   * addItem: boolean to add or remove the item from the list
   * isSubReason : If a sub reason is checked / unchecked
   */
  // Takes a parent reason or one of its children; only the parent carries `children`.
  const addRemoveReasons = (
    reason: Pick<CancellationReason, 'id'> & Partial<Pick<CancellationReason, 'children'>>,
    addItem: boolean,
    isSubReason: boolean,
  ) => {
    if (addItem) {
      props.setCancellationReasonIds([...props.cancellationReasonIds, reason.id])
      if (isSubReason) setSelectedSubOptions([...selectedSubOptions, reason.id])
    } else {
      let filteredData = props.cancellationReasonIds.filter(
        selectedReasonId => selectedReasonId !== reason.id,
      )
      let subOptionFilteredData = selectedSubOptions.filter(
        selectedReasonId => selectedReasonId !== reason.id,
      )
      if (reason.children && reason.children.length > 0) {
        filteredData = filteredData.filter(
          selectedReasonId => !selectedSubOptions.includes(selectedReasonId),
        )
        subOptionFilteredData = []
      }
      props.setCancellationReasonIds(filteredData)
      if (isSubReason) setSelectedSubOptions(subOptionFilteredData)
    }
  }

  /** API call to fetch list of cancellation reasons */
  const query = useQuery(appointmentCancellationReasons.listQueryKey(), async () => {
    const response = await appointmentCancellationReasons.list()
    return response.data
  })
  const displayCancellationReasons = query.data ? groupCancellationReasons(query.data) : []

  if (query.isPending) {
    return null
  }
  if (query.isError) {
    return null
  }

  return (
    <FFModal
      open={true}
      style={{ width: '65%', marginLeft: 'auto', marginRight: 'auto' }}
      header={
        <Box sx={{ m: 2 }}>
          <Typography variant="h6">{'Cancel Appointment'}</Typography>
        </Box>
      }
      footer={
        <Box sx={{ m: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                props.onClose(false)
              }}
              variant="text"
              size="small"
              sx={{
                borderRadius: 10,
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '20%',
                marginRight: 1,
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>{'Exit'}</Typography>
            </Button>
            <Button
              disabled={props.cancellationReasonIds.length === 0}
              variant="contained"
              size="small"
              onClick={() => {
                props.onClose(true)
              }}
              sx={{
                borderRadius: 10,
                fontSize: '1.4rem',
                maxHeight: '75%',
                width: '45%',
              }}
            >
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 500 }}>
                {'Cancel Appointment'}
              </Typography>
            </Button>
          </Box>
        </Box>
      }
    >
      <>
        <Box sx={{ m: 2 }}>
          {displayCancellationReasons.map((category, index) => {
            return (
              <Box key={'category-' + index}>
                <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                  {category.label}
                </Typography>
                <Box sx={{ ml: 2 }}>
                  {category.children.map((reason, i) => {
                    return (
                      <FormGroup key={'reason-' + i}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={e => {
                                onReasonCheckboxClick(e, reason)
                              }}
                            />
                          }
                          label={reason.label}
                          sx={{
                            color: 'rgba(0, 0, 0, 0.87)',
                          }}
                        />
                        {reason.is_patient_facing && reason.uid == UidMapping.otherPatient ? (
                          <Box sx={{ display: showOtherTextField ? 'block' : 'none' }}>
                            <TextField
                              id="outlined-basic"
                              variant="outlined"
                              value={props.otherReasonForPatient}
                              onChange={e => props.setOtherReasonForPatient(e.target?.value)}
                              multiline={true}
                              fullWidth
                              placeholder="Add reason (optional)"
                            />
                          </Box>
                        ) : null}
                        <Box sx={{ ml: 2, display: showSubOptions ? 'block' : 'none' }}>
                          {reason?.children &&
                            reason?.children?.length > 0 &&
                            reason?.children?.map((subReason, key) => {
                              return (
                                <FormGroup key={'sub-' + key}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={props.cancellationReasonIds.includes(subReason.id)}
                                        onChange={e => {
                                          if (
                                            !subReason.is_patient_facing &&
                                            subReason.uid == UidMapping.otherFirefly
                                          ) {
                                            setShowOtherFireflyTextField(e.target.checked)
                                            if (!e.target.checked) {
                                              props.setOtherReasonForFirefly('')
                                            }
                                          }
                                          addRemoveReasons(subReason, e.target.checked, true)
                                        }}
                                      />
                                    }
                                    label={subReason.label}
                                    sx={{
                                      color: 'rgba(0, 0, 0, 0.87)',
                                    }}
                                  />

                                  {!subReason.is_patient_facing &&
                                  subReason.uid == UidMapping.otherFirefly ? (
                                    <Box
                                      sx={{ display: showOtherFireflyTextField ? 'block' : 'none' }}
                                    >
                                      <TextField
                                        id="outlined-basic"
                                        variant="outlined"
                                        value={props.otherReasonForFirefly}
                                        onChange={e =>
                                          props.setOtherReasonForFirefly(e.target?.value)
                                        }
                                        multiline={true}
                                        fullWidth
                                        placeholder="Add reason (optional)"
                                      />
                                    </Box>
                                  ) : null}
                                </FormGroup>
                              )
                            })}
                        </Box>
                      </FormGroup>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
        </Box>
        <Box sx={{ m: 2 }}>
          <IconButton
            onClick={() => {
              props.onClose(false)
            }}
            sx={{
              position: 'absolute',
              zIndex: 100,
              top: 2,
              right: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </>
    </FFModal>
  )
}

export default CancelAppointmentModal
